import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TABLE_PARAM, SENT_TO_TYPE, STATUSES } from 'src/app/shared/constants/common.constants';
import {
  GetNotificationResponse,
  NotificationListData,
  NotificationParams,
  NotificationItem,
} from 'src/app/shared/model/notification.model';
import httpErrorCodes from 'src/app/core/common/http_error_codes';
import { CommonService } from 'src/app/shared/services/common.service';
import { PaginationService } from 'src/app/shared/providers/pagination/pagination.service';

@Component({
  selector: 'app-notification-history',
  standalone: true,
  imports: [CommonModule, TableModule, TranslateModule, ToastModule, ConfirmDialogModule],
  providers: [TranslateService, ConfirmationService],
  templateUrl: './notification-history.component.html',
  styleUrl: './notification-history.component.scss',
})
export class NotificationHistoryComponent implements OnInit {
  tableParams = TABLE_PARAM;

  noDataRow = [{}];

  first = 0;

  rows = TABLE_PARAM.PAGE_SIZE;

  sortField = 'SentOn';

  sortOrder = TABLE_PARAM.SORT_DEC_ORDER;

  totalRecords = 0;

  notificationData: GetNotificationResponse['data'] | null = null;

  notificationItems: NotificationListData['items'] | [] = [];

  notificationPageNumber: NotificationListData['pageNumber'] = TABLE_PARAM.PAGE_NUMBER;

  notificationPageSize: NotificationListData['pageSize'] = TABLE_PARAM.PAGE_SIZE;

  notificationTitle!: string;

  notificationMessage!: string;

  constructor(
    private notificationService: NotificationService,
    private toastService: ToasterService,
    private translateService: TranslateService,
    private confirmationService: ConfirmationService,
    private cdRef: ChangeDetectorRef,
    private commonService: CommonService,
    private paginationService: PaginationService,
  ) {}

  ngOnInit(): void {
    this.rows = this.paginationService.getPageSize('notification-history');
    this.tableParams.ROW_PER_PAGE = this.paginationService.getAllowedSizes();
    this.notificationPageSize = this.rows;
    this.notificationPageNumber = 1;
    this.getNotificationListData(
      this.notificationPageNumber,
      this.notificationPageSize,
      this.sortField,
      this.sortOrder,
    );
  }

  getConvertedDateTime(utcDate: string) {
    return CommonService.getFormattedDateTime(utcDate);
  }

  pageChange(event: { first: number; rows: number }) {
    this.first = event.first;
    this.rows = event.rows;
    this.paginationService.setPageSize('notification-history', this.rows);
    this.notificationPageNumber = this.first / this.rows;
    this.notificationPageSize = this.rows;
    this.sortField = this.sortField === 'SendTo' ? 'SentOn' : this.sortField;
    this.getNotificationListData(
      this.notificationPageNumber + 1,
      this.rows,
      this.sortField,
      this.sortOrder,
    );
  }

  sortChange(event: { field: string; order: string }) {
    this.first = 0;
    this.notificationPageNumber =
      this.notificationPageNumber === 0 ? 1 : this.notificationPageNumber;
    this.sortField = event.field;
    this.sortOrder =
      String(event.order) === '1'
        ? this.tableParams.SORT_ASC_ORDER
        : this.tableParams.SORT_DEC_ORDER;

    if (this.sortField === 'SendTo') {
      this.notificationItems.sort((a: NotificationItem, b: NotificationItem) => {
        const recipientA = this.getNotificationRecipient(a);
        const recipientB = this.getNotificationRecipient(b);

        if (this.sortOrder === this.tableParams.SORT_ASC_ORDER) {
          return recipientA.localeCompare(recipientB);
        }

        return recipientB.localeCompare(recipientA);
      });
    } else {
      this.sortField = this.sortField === 'SendTo' ? 'SentOn' : this.sortField;
      this.getNotificationListData(
        this.notificationPageNumber,
        this.notificationPageSize,
        this.sortField,
        this.sortOrder,
      );
    }
  }

  getNotificationRecipient(notification: {
    sentToCallers: boolean;
    sentToTalkingBuddies: boolean;
  }): string {
    if (notification.sentToCallers && notification.sentToTalkingBuddies) {
      return SENT_TO_TYPE.ALL_USERS;
    }

    if (notification.sentToCallers) {
      return SENT_TO_TYPE.ALL_CALLERS;
    }

    if (notification.sentToTalkingBuddies) {
      return SENT_TO_TYPE.ALL_TALKING_BUDDIES;
    }

    return '';
  }

  showNotificationPopup(event: Event, notificationTitle: string, notificationMessage: string) {
    this.notificationTitle = notificationTitle;
    this.notificationMessage = notificationMessage;
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      header: notificationTitle,
      message: notificationMessage,
      blockScroll: true,
    });
  }

  closeConfirmDialog() {
    this.confirmationService.close();
  }

  getNotificationListData(
    pageNumber: number,
    pageSize: number,
    sortField: string,
    sortOrder: string,
  ): void {
    const params: NotificationParams = {
      pageNumber,
      pageSize,
      sortField,
      sortOrder,
    };
    this.notificationService.getNotifications(params).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.notificationData = response.data;
          this.notificationItems = this.notificationData?.items;
          this.totalRecords = this.notificationData.totalCount;
          this.cdRef.detectChanges();
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'NOTIFICATION_PAGE.TOAST_MESSAGE.FAILED_LOAD_NOTIFICATION_DATA',
            ),
          );
        }
      },
    });
  }
}
